// CrystalVault — Overview Page

import { VAULT_CONFIGS, VAULT_ABI, USDC_DECIMALS, SHARE_DECIMALS, EXPLORER, APP_MODE } from '../config.js';
import { getAddress, getSigner, getReadProvider, shortAddr, isViewOnly } from '../wallet.js';
import { showTxModal, txModalSuccess, txModalError } from '../tx.js';
import { getVaultState, prefetchVaultStates, seedFleetState } from '../hlFeed.js';
import { computeWithdrawalFee } from '../vault-math.js';
import { isStealth, isDepositor, canBypassStealth, launchDateLabel, startCountdown, xFollowUrl, xShareUrl, registerEarlyVisitor } from '../stealth-mode.js';
import { tzZone, tzLabel } from '../time.js';

// Global claim handler invoked by the Pending Withdrawals row's inline onclick.
// Defined at module scope (not inside initOverview) so it survives page
// re-renders and remains referenceable from inline HTML.
window._claimWithdraw = async function(vaultAddr) {
  const signer = getSigner();
  if (!signer) { txModalError("Connect your wallet first"); return; }
  const vaultW = new ethers.Contract(vaultAddr, VAULT_ABI, signer);
  try {
    showTxModal("Claiming USDC", "Please confirm in MetaMask...");
    const tx = await vaultW.claimWithdraw({ gasLimit: 200000 });
    const receipt = await tx.wait();
    txModalSuccess("USDC claimed", receipt.hash);
    // Re-render current page to refresh pending-withdrawals state
    if (typeof window._rerender === 'function') window._rerender();
    else if (window.location.hash === '#overview' || window.location.hash === '') initOverview();
  } catch (e) {
    txModalError(e.reason || e.shortMessage || e.message || "Claim failed");
  }
};

// Withdraw the full remaining balance from a RETIRED vault. Module-scope so
// the inline onclick survives re-renders. Retired vaults are invisible to the
// server (load_fleet is live-only), so this is the holder's ONLY withdraw
// path — a pure on-chain requestWithdraw against the whole position. The
// subsequent claim reuses window._claimWithdraw after the timelock elapses.
// See GEMSTONE_RETIREMENT_DESIGN.md §5.
window._requestRetiredWithdraw = async function(vaultAddr) {
  const signer = getSigner();
  if (!signer) { txModalError("Connect your wallet first"); return; }
  const vault = new ethers.Contract(vaultAddr, VAULT_ABI, signer);
  try {
    const addr = getAddress();
    const sharesBn = BigInt(await vault.balanceOf(addr));
    if (sharesBn <= 0n) { txModalError("No shares to withdraw"); return; }
    showTxModal("Requesting Withdrawal", "Please confirm in MetaMask...");
    const tx = await vault.requestWithdraw(sharesBn, { gasLimit: 300000 });
    const receipt = await tx.wait();
    txModalSuccess("Withdrawal requested. USDC will be claimable after a short timelock.", receipt.hash);
    window.dispatchEvent(new Event("wallet-changed"));
    if (typeof window._rerender === 'function') window._rerender();
    else if (window.location.hash === '#overview' || window.location.hash === '') initOverview();
  } catch (e) {
    txModalError(e.reason || e.shortMessage || e.message || "Withdrawal failed");
  }
};

function getApiBase() { return typeof window.CC_API_BASE === 'string' ? window.CC_API_BASE : ''; }

// Disconnected state: Overview is a personal dashboard — nothing to show
// without a user scope. Show a single clear prompt instead of pretending
// with $0 cards, aggregate charts, and other vaults' trades.
function renderDisconnectedOverview() {
  return `
  <div class="flex flex-col items-center justify-center py-24 px-6 text-center">
    <div class="w-16 h-16 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mb-6">
      <span class="material-symbols-outlined text-primary text-3xl">account_balance_wallet</span>
    </div>
    <h2 class="font-headline font-bold text-2xl text-on-surface mb-3">Connect your wallet</h2>
    <p class="text-on-surface-variant max-w-md mb-8">
      Your portfolio value, positions, and performance appear here once your wallet is connected.
    </p>
    <button id="overview-connect-btn" class="px-6 py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary font-headline font-bold rounded-DEFAULT hover:shadow-[0_0_24px_rgba(138,235,255,0.4)] transition-all duration-300 active:scale-[0.98]">
      Connect Wallet
    </button>
    <a href="#vaults" class="mt-6 text-sm text-secondary hover:text-primary transition-colors">
      Or browse the vaults →
    </a>
  </div>
  `;
}

// Stealth-mode view (connected). No P&L, no charts, no other vaults' trades.
// A single block: launch countdown + "You're #N" + balance check + share button.
function renderStealthConnected() {
  return `
  <div class="flex flex-col items-center justify-center py-16 px-6 text-center">
    <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-surface-container-low ghost-border mb-8">
      <span class="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
      <span class="text-[10px] uppercase tracking-widest text-secondary-fixed-dim font-bold">You're Early</span>
    </div>
    <h2 class="font-headline font-bold text-3xl md:text-4xl text-on-surface mb-3">
      Deposits open <span class="text-primary">${launchDateLabel()}</span>
    </h2>
    <p class="text-on-surface-variant max-w-md mb-8">
      You've locked in your spot. Onyx and Amber are trading live today —
      performance metrics and deposits unlock at launch.
    </p>

    <div id="overview-countdown" class="mb-10"></div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl mb-10">
      <div class="bg-surface-container rounded-xl border border-primary/20 p-5">
        <p class="text-[0.65rem] uppercase tracking-widest text-secondary/60 mb-1">Your position</p>
        <p class="text-3xl font-headline font-bold text-primary" id="stealth-position">—</p>
      </div>
      <div class="bg-surface-container rounded-xl border border-outline-variant/10 p-5">
        <p class="text-[0.65rem] uppercase tracking-widest text-secondary/60 mb-1">Live vaults trading</p>
        <p class="text-3xl font-headline font-bold text-on-surface" id="stealth-live-count">—</p>
        <p class="text-[0.7rem] text-secondary/60 mt-1">See them in the Trades tab</p>
      </div>
    </div>

    <div class="flex flex-col sm:flex-row items-center gap-4">
      <a id="stealth-share-x" href="#" target="_blank" rel="noopener"
         class="w-full sm:w-auto px-6 py-3 bg-gradient-to-br from-primary to-primary-container text-on-primary-container font-bold transition-all hover:scale-105 flex items-center justify-center gap-2">
        <span class="material-symbols-outlined text-lg">share</span>
        Share on X
      </a>
      <a href="${xFollowUrl()}" target="_blank" rel="noopener"
         class="w-full sm:w-auto px-6 py-3 glass-panel ghost-border text-on-surface font-semibold hover:bg-surface-variant/50 transition-all flex items-center justify-center gap-2">
        <span class="material-symbols-outlined text-lg">notifications_active</span>
        Follow ${APP_MODE.x_handle}
      </a>
    </div>
  </div>
  `;
}


export function renderOverview() {
  if (!getAddress()) return renderDisconnectedOverview();
  // Read-only preview banner (?view=0x… mode) — no signer, viewing someone's positions.
  const viewBanner = isViewOnly()
    ? `<div class="mb-4 px-4 py-2 rounded-lg bg-tertiary/10 border border-tertiary/30 text-xs text-on-surface-variant flex items-center gap-2">
         <span class="material-symbols-outlined text-tertiary text-base">visibility</span>
         Read-only preview of <span class="font-mono text-on-surface">${shortAddr(getAddress())}</span> — connect your own wallet to deposit or withdraw.
       </div>`
    : "";
  // In stealth, default to the stealth view. initOverview() will swap in the
  // live HTML for wallets that are already depositors (async RPC check).
  if (isStealth()) return viewBanner + renderStealthConnected();
  return viewBanner + _renderOverviewLive();
}

// ── Skeleton helpers ─────────────────────────────────────────────────────────
// Build N table rows pre-shaped to the same column structure as the real data
// row so layout doesn't shift when /api/me/portfolio resolves. The shimmer
// animation comes from the .skel CSS rule in app.html.
function _skelPositionRows(n) {
  // 6 cols: Vault | Shares | Share Price | Value | Cost Basis | P&L
  const row = `
    <tr>
      <td class="py-3"><div class="flex items-center gap-2">
        <span class="skel" style="height:1.75rem;width:1.75rem;border-radius:6px"></span>
        <span class="skel" style="height:0.95rem;width:5rem"></span>
      </div></td>
      <td class="py-3 text-right"><span class="skel" style="height:0.9rem;width:3.5rem"></span></td>
      <td class="py-3 text-right"><span class="skel" style="height:0.9rem;width:3rem"></span></td>
      <td class="py-3 text-right"><span class="skel" style="height:0.9rem;width:3rem"></span></td>
      <td class="py-3 text-right"><span class="skel" style="height:0.9rem;width:3rem"></span></td>
      <td class="py-3 text-right"><span class="skel" style="height:0.9rem;width:3rem"></span></td>
    </tr>`;
  return row.repeat(n);
}
function _skelTradeRows(n) {
  // 8 cols: Vault | Coin | Side | Entry | Current | Stop Loss | Size | P&L
  const row = `
    <tr>
      <td class="py-3"><div class="flex items-center gap-2">
        <span class="skel" style="height:1.5rem;width:1.5rem;border-radius:6px"></span>
        <span class="skel" style="height:0.85rem;width:4rem"></span>
      </div></td>
      <td class="py-3"><span class="skel" style="height:0.85rem;width:2.5rem"></span></td>
      <td class="py-3"><span class="skel" style="height:0.85rem;width:2.5rem"></span></td>
      <td class="py-3 text-right"><span class="skel" style="height:0.85rem;width:3rem"></span></td>
      <td class="py-3 text-right"><span class="skel" style="height:0.85rem;width:3rem"></span></td>
      <td class="py-3 text-right"><span class="skel" style="height:0.85rem;width:3rem"></span></td>
      <td class="py-3 text-right"><span class="skel" style="height:0.85rem;width:2.5rem"></span></td>
      <td class="py-3 text-right"><span class="skel" style="height:0.85rem;width:3rem"></span></td>
    </tr>`;
  return row.repeat(n);
}

function _renderOverviewLive() {
  return `
  <!-- Stats — depositor-focused, single headline P&L (industry pattern:
       Yearn/Morpho/Enzyme/dYdX/Hyperliquid vaults all show one NAV-based
       return on the home screen). Realized/Unrealized decomposition moved
       to the Trades page where it describes vault mechanics. -->
  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
    <div class="bg-surface-container rounded-xl border border-outline-variant/10 p-5">
      <p class="text-[0.65rem] uppercase tracking-widest text-secondary/60 mb-1">Portfolio Value</p>
      <p class="text-2xl font-headline font-bold text-primary" id="total-value"><span class="skel" style="height:1.5rem;width:7rem"></span></p>
    </div>
    <div class="bg-surface-container rounded-xl border border-outline-variant/10 p-5">
      <p class="text-[0.65rem] uppercase tracking-widest text-secondary/60 mb-1">Total Deposited</p>
      <p class="text-2xl font-headline font-bold text-on-surface" id="total-deposited"><span class="skel" style="height:1.5rem;width:7rem"></span></p>
    </div>
    <div class="bg-surface-container rounded-xl border border-outline-variant/10 p-5 glow">
      <p class="text-[0.65rem] uppercase tracking-widest text-secondary/60 mb-1">Total Return</p>
      <p class="text-2xl font-headline font-bold text-tertiary" id="total-return" title="Your complete net profit: everything you hold now plus everything you've withdrawn, minus everything you deposited. Counts realized gains; net of all fees."><span class="skel" style="height:1.5rem;width:6rem"></span></p>
      <p class="text-[0.7rem] text-secondary/60 mt-1" id="total-return-pct"><span class="skel" style="height:0.7rem;width:8rem"></span></p>
    </div>
  </div>

  <!-- Open Trades -->
  <div class="bg-surface-container rounded-xl border border-outline-variant/10 p-6 mb-8">
    <h3 class="font-headline font-bold text-lg text-on-surface mb-4 flex items-center gap-2">
      <span class="material-symbols-outlined text-primary">candlestick_chart</span>
      Open Trades
      <span class="text-[0.6rem] font-normal text-secondary/40 tracking-wider uppercase ml-1">snapshot · refresh to update</span>
    </h3>
    <div id="open-trades-container">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-xs uppercase tracking-wider text-secondary/60 border-b border-outline-variant/10">
            <th class="text-left py-2">Vault</th>
            <th class="text-left py-2">Coin</th>
            <th class="text-left py-2">Side</th>
            <th class="text-right py-2">Entry</th>
            <th class="text-right py-2">Current</th>
            <th class="text-right py-2">Stop Loss</th>
            <th class="text-right py-2">Size</th>
            <th class="text-right py-2">P&amp;L</th>
          </tr>
        </thead>
        <tbody id="open-trades-body" class="divide-y divide-outline-variant/5">
          ${_skelTradeRows(8)}
        </tbody>
      </table>
    </div>
  </div>

  <!-- Vault Positions -->
  <div class="bg-surface-container rounded-xl border border-outline-variant/10 p-6 mb-8">
    <h3 class="font-headline font-bold text-lg text-on-surface mb-4 flex items-center gap-2">
      <span class="material-symbols-outlined text-primary">account_balance</span>
      Your Vault Positions
    </h3>
    <div id="vault-positions">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-xs uppercase tracking-wider text-secondary/60 border-b border-outline-variant/10">
            <th class="text-left py-2">Vault</th>
            <th class="text-right py-2" title="Number of vault shares you hold. Shares are minted when you deposit USDC; their dollar value moves with the vault's share price.">Shares</th>
            <th class="text-right py-2" title="Current $ value of one vault share.">Share Price</th>
            <th class="text-right py-2" title="Current value of your position = shares × share price.">Value</th>
            <th class="text-right py-2" title="Cost Basis — the average share price you paid. Your unrealised P&L = current share price minus cost basis, scaled by your shares.">Cost Basis</th>
            <th class="text-right py-2" title="Profit and loss on your position vs. your cost basis. Unrealised — only locked in when you withdraw.">P&L</th>
          </tr>
        </thead>
        <tbody id="positions-body" class="divide-y divide-outline-variant/5">
          ${_skelPositionRows(6)}
        </tbody>
      </table>
    </div>
  </div>

  <!-- Pending Withdrawals -->
  <div id="pending-section" class="hidden bg-surface-container rounded-xl border border-tertiary/20 p-6 mb-8">
    <h3 class="font-headline font-bold text-lg text-tertiary mb-4 flex items-center gap-2">
      <span class="material-symbols-outlined">schedule</span>
      Pending Withdrawals
    </h3>
    <div id="pending-body"></div>
  </div>

  <!-- Retired vaults — withdraw only. Populated client-side by
       _scanRetiredHoldings() against any status="retired" vault address in
       config.js. Shows ONLY to wallets that still hold shares (or a pending
       withdrawal). See GEMSTONE_RETIREMENT_DESIGN.md §5. -->
  <div id="retired-section" class="hidden bg-surface-container rounded-xl border border-outline-variant/20 p-6 mb-8">
    <h3 class="font-headline font-bold text-lg text-on-surface mb-1 flex items-center gap-2">
      <span class="material-symbols-outlined text-on-surface-variant">inventory_2</span>
      Retired vaults — withdraw only
    </h3>
    <p class="text-xs text-on-surface-variant/60 mb-4">These vaults are no longer active. You can withdraw your remaining balance anytime.</p>
    <div id="retired-body"></div>
  </div>

  <!-- Performance Chart -->
  <div class="bg-surface-container rounded-xl border border-outline-variant/10 p-6">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-3">
        <h3 class="font-headline font-bold text-lg text-on-surface flex items-center gap-2">
          <span class="material-symbols-outlined text-primary">show_chart</span>
          Performance
        </h3>
        <div class="flex gap-1" id="chart-mode-pills">
          <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-primary text-on-primary" data-chart-mode="total">Total</button>
          <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/20 text-on-surface-variant hover:text-primary" data-chart-mode="vaults">Vaults</button>
        </div>
        <div class="flex gap-1" id="chart-units-pills">
          <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-primary text-on-primary" data-chart-units="dollar">$</button>
          <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/20 text-on-surface-variant hover:text-primary" data-chart-units="percent">%</button>
        </div>
      </div>
      <div class="flex gap-1" id="chart-tf-pills">
        <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/20 text-on-surface-variant hover:text-primary" data-chart-tf="perpWeek">7D</button>
        <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-primary text-on-primary" data-chart-tf="perpMonth">1M</button>
        <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/20 text-on-surface-variant hover:text-primary" data-chart-tf="perpQuarter">3M</button>
        <button class="px-3 py-1.5 rounded text-[0.65rem] font-bold uppercase tracking-wider transition-colors bg-surface-container-lowest ring-1 ring-outline-variant/20 text-on-surface-variant hover:text-primary" data-chart-tf="perpAllTime">All</button>
      </div>
    </div>
    <div class="h-72" id="chart-container">
      <canvas id="perf-chart"></canvas>
    </div>
  </div>
  `;
}

export async function initOverview() {
  const addr = getAddress();

  // Disconnected state: nothing to load. Just wire the Connect CTA to the
  // same handler the top-nav Connect Wallet button uses.
  if (!addr) {
    document.getElementById("overview-connect-btn")?.addEventListener("click", () => {
      document.getElementById("connect-btn")?.click();
    });
    return;
  }

  // Stealth-mode connected view: depositors get the full public dashboard
  // (metrics/P&L/chart/withdraw). Whitelisted testers also get the live UI
  // — and unlike normal depositors, the deposit CTA is left enabled for them
  // (handled in deposit.js / vault-detail.js). Everyone else stays on the
  // countdown view.
  if (isStealth()) {
    if (canBypassStealth(addr) || await isDepositor(addr)) {
      document.getElementById("page-content").innerHTML = _renderOverviewLive();
      await _initOverviewLive(addr);
      return;
    }
    await _initOverviewStealth(addr);
    return;
  }

  await _initOverviewLive(addr);
}

async function _initOverviewStealth(addr) {
  startCountdown(document.getElementById("overview-countdown"));
  const liveCount = VAULT_CONFIGS.filter(v => v.status === "live" && v.address && (v.visibility ?? "public") === "public").length;
  const liveEl = document.getElementById("stealth-live-count");
  if (liveEl) liveEl.textContent = String(liveCount);

  // Register as early visitor (dedups server-side)
  const resp = await registerEarlyVisitor(addr);
  const position = resp?.position;
  const posEl = document.getElementById("stealth-position");
  if (posEl && position != null) posEl.textContent = `#${position}`;

  // Wire share button with dynamic position
  const shareEl = document.getElementById("stealth-share-x");
  if (shareEl) shareEl.href = xShareUrl({ position });
}

async function _initOverviewLive(addr) {
  const rp = getReadProvider();

  // ── Combined Overview bundle ──────────────────────────────────────
  // Single request to /api/me/overview replaces the 4 parallel calls
  // that used to race each other and each cold-start their own Lambda
  // container. Inside Lambda, the same 4 fetches still happen in
  // parallel (ThreadPoolExecutor) but cold-start tax is paid once.
  // See crystalclear/api/main.py:get_me_overview for the contract.
  //
  // Fallback: if the bundle endpoint isn't available (5xx, network,
  // older Lambda version), each consumer falls back to its own
  // individual fetch — same behavior as before this change.
  const bundlePromise = fetch(`${getApiBase()}/api/me/overview?addr=${addr}`, { cache: "no-cache" })
    .then((r) => r.ok ? r.json() : null)
    .catch((e) => { console.warn("me/overview bundle failed; falling back to individual fetches:", e); return null; });

  // Seed the hlFeed per-address cache as soon as the bundle arrives —
  // makes prefetchVaultStates() a cache-hit later in _loadOpenTrades.
  bundlePromise.then((b) => {
    if (b?.hl_state?.addrs) seedFleetState(b.hl_state.addrs);
  });

  // mePromise — used by KPI cards + chart + _loadOpenTrades. Prefer the
  // bundle's portfolio slice; fall back to /api/me/portfolio if absent.
  const mePromise = bundlePromise.then(async (b) => {
    if (b?.portfolio) return b.portfolio;
    try {
      const r = await fetch(`${getApiBase()}/api/me/portfolio?addr=${addr}`, { cache: "no-cache" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.json();
    } catch (e) {
      console.error("me/portfolio fallback fetch failed:", e);
      return { vaults: [] };
    }
  });

  // Chart and open trades are public — render for connected users only so
  // the disconnected view stays clean.
  //
  // Ordering: the Open Trades table is the user-critical element on this
  // page, so its 3 API calls (attribution + fleet/positions + fleet/hl-state)
  // get the warm-Lambda pool first. The performance chart fires 6 more
  // calls to /api/hl/info (one per vault) for its TVL history, which we
  // gate on `openTradesReady` so they don't compete with the table burst
  // for PC=1. Result: critical-path burst drops from 10 → 4 concurrent
  // Lambda invocations, cold spillover tail shrinks from ~5s → ~1.5s.
  let _openTradesReady;
  const openTradesReady = new Promise((r) => { _openTradesReady = r; });
  _buildPerformanceChart(mePromise, openTradesReady);   // gates its heavy fetches on the gate
  _loadOpenTrades(mePromise, bundlePromise).finally(() => _openTradesReady());
  // USDC balance card was removed — same number is shown next to the wallet
  // address in the header, no need to duplicate.

  // One backend call replaces 36+ in-browser RPC reads. /api/me/portfolio
  // batches all eth_calls server-side over a keep-alive HTTPS Session;
  // 5s per-user cache there for repeat hits. Frontend computes value_raw
  // and assets_per_share locally from totalAssets+totalSupply using the
  // V3.6 contract's mulDiv formula:
  //     value = shares × (totalAssets + 1) / (totalSupply + 10^3)
  // Same math the pending-withdrawal grossPending block already uses — no
  // contract drift risk.
  const me = await mePromise;

  // Match server-returned vaults back to client-side VAULT_CONFIGS so we
  // keep icon/color/launchSharePrice without round-tripping them. Server
  // already filtered out zero-stake vaults.
  const vcByAddr = {};
  for (const vc of VAULT_CONFIGS) {
    if (vc.address) vcByAddr[vc.address.toLowerCase()] = vc;
  }
  const VIRTUAL_OFFSET = 1000n;  // 10^_decimalsOffset = 10^3 for cvUSDC

  const perVault = (me.vaults || []).map((v) => {
    const vc = vcByAddr[v.address.toLowerCase()];
    if (!vc) return null;
    try {
      const shares = BigInt(v.shares);
      const costBasis = BigInt(v.user_cost_basis);
      const totalAssets = BigInt(v.total_assets);
      const totalSupply = BigInt(v.total_supply);
      const withdrawReq = (v.withdraw_req || []).map(BigInt);
      const perfFeeBps = BigInt(v.perf_fee_bps || 0);

      const pendingShares = withdrawReq[0] || 0n;
      const oneShare = 10n ** BigInt(SHARE_DECIMALS);

      // V3.6 mulDiv formula for convertToAssets — same one the contract uses
      // (OZ ERC4626Upgradeable._convertToAssets with _decimalsOffset=3).
      const denom = totalSupply + VIRTUAL_OFFSET;
      const needsAssetsPerShare = totalSupply > 0n && totalAssets > 0n;
      const assetsPerShareRaw = needsAssetsPerShare
        ? (oneShare * (totalAssets + 1n)) / denom : 0n;
      const valueRawWei = shares > 0n
        ? (shares * (totalAssets + 1n)) / denom : 0n;

      let sharePrice = 1.0;
      if (needsAssetsPerShare) {
        const rawPrice = Number(assetsPerShareRaw) / 10 ** USDC_DECIMALS;
        sharePrice = rawPrice / (vc.launchSharePrice || 1.0);
      }

      const sharesHuman = Number(shares) / 10 ** SHARE_DECIMALS;
      const valueRaw = Number(valueRawWei) / 10 ** USDC_DECIMALS;
      const costHuman = Number(costBasis) / 10 ** USDC_DECIMALS;

      // 🔴 V3.6: requestWithdraw moves shares OUT of balanceOf immediately, so
      // `shares` (= balanceOf, from the API) ALREADY excludes any pending
      // withdrawal — valueRaw is the held position only. Do NOT subtract the
      // pending amount again: that double-counts and understates the position
      // for the whole pending window (Ruby showed $20.77 for 22 shares actually
      // worth $32.64 on 2026-06-15; correct again the instant it was claimed).
      // The pending withdrawal is surfaced separately in pendingRow below. (Prior
      // V3.4 code subtracted here because balanceOf used to keep the requested
      // shares until claim — no longer true on V3.6.)
      const valueHuman = valueRaw;
      const pnl = valueHuman - costHuman;

      const pnlClass = pnl >= 0 ? "text-emerald-400" : "text-error";
      const row = `
        <tr>
          <td class="py-3">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 ${vc.color.bg10} rounded-md flex items-center justify-center">
                <span class="material-symbols-outlined ${vc.color.text} text-base">${vc.icon}</span>
              </div>
              <span class="font-headline font-semibold ${vc.color.text}">${vc.name}</span>
            </div>
          </td>
          <td class="py-3 text-right">${sharesHuman.toFixed(4)}</td>
          <td class="py-3 text-right">$${sharePrice.toFixed(4)}</td>
          <td class="py-3 text-right font-semibold">$${valueHuman.toFixed(2)}</td>
          <td class="py-3 text-right text-secondary/60">$${costHuman.toFixed(2)}</td>
          <td class="py-3 text-right font-semibold ${pnlClass}">$${pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}</td>
        </tr>
      `;

      let pendingRow = null;
      if (pendingShares > 0n) {
        const costBasisSnap = withdrawReq[1];
        const totalSharesSnap = withdrawReq[2];
        // V3.6 locks the gross-asset payout in snapshottedAssets at request
        // time. Use it directly — that's what claimWithdraw will pay.
        // The live-recompute fallback is only for legacy pre-V3.6 pending
        // requests where snapshottedAssets == 0. See bug 2026-05-19 Diamond
        // ($10.26 UI vs $24.02 actual). API already returns 5-tuple for
        // withdraw_req so withdrawReq[4] is populated.
        const snapshottedAssets = withdrawReq[4] || 0n;
        const grossAssets = snapshottedAssets > 0n
          ? snapshottedAssets
          : (pendingShares * (totalAssets + 1n)) / (totalSupply + pendingShares + VIRTUAL_OFFSET);
        const fee = computeWithdrawalFee(pendingShares, totalSharesSnap, costBasisSnap, grossAssets, perfFeeBps);
        const net = grossAssets - fee;
        const grossHuman = Number(grossAssets) / 10 ** USDC_DECIMALS;
        const feeHuman = Number(fee) / 10 ** USDC_DECIMALS;
        const netHuman = Number(net) / 10 ** USDC_DECIMALS;
        const tail = snapshottedAssets > 0n
          ? "Amount is locked: this is exactly what the contract will pay on claim."
          : "Amount fluctuates with vault NAV until claimed.";
        const feeTitle = fee > 0n
          ? `Gross $${grossHuman.toFixed(2)} − 20% perf fee $${feeHuman.toFixed(4)} = net $${netHuman.toFixed(2)}. ${tail}`
          : tail;
        pendingRow = `
          <div class="flex justify-between items-center p-3 bg-tertiary/5 rounded-lg mb-2">
            <div class="flex items-center gap-2">
              <span class="font-semibold text-on-surface">${vc.name}</span>
              <span class="text-secondary/60 text-sm">~$${netHuman.toFixed(2)} USDC ready to claim</span>
              <span class="material-symbols-outlined text-secondary/40 text-sm cursor-help" title="${feeTitle}">info</span>
            </div>
            <button class="px-4 py-1.5 bg-tertiary/20 hover:bg-tertiary/30 border border-tertiary/30 rounded-lg text-tertiary font-headline text-sm"
                    onclick="window._claimWithdraw('${vc.address}')">Claim</button>
          </div>
        `;
      }

      return { row, pendingRow, valueHuman, costHuman };
    } catch (e) {
      console.error(`Error decoding ${vc.name}:`, e);
      return null;
    }
  });

  // Aggregate. Order in `perVault` matches `liveVaults` (Promise.all preserves
  // input order), which matches VAULT_CONFIGS order — same UI sort as before.
  let totalValue = 0;
  let totalCost = 0;
  const rows = [];
  const pendingRows = [];
  for (const r of perVault) {
    if (!r) continue;
    rows.push(r.row);
    if (r.pendingRow) pendingRows.push(r.pendingRow);
    totalValue += r.valueHuman;
    totalCost += r.costHuman;
  }

  if (rows.length > 0) {
    document.getElementById("positions-body").innerHTML = rows.join("");
  } else {
    document.getElementById("positions-body").innerHTML =
      '<tr><td colspan="6" class="py-8 text-center text-secondary/40">No vault positions. Go to Deposit to get started.</td></tr>';
  }

  // Assign pending withdrawals once at the end (single mutation, idempotent
  // even if initOverview() runs twice from concurrent renders).
  const pendingBody = document.getElementById("pending-body");
  const pendingSection = document.getElementById("pending-section");
  if (pendingBody) pendingBody.innerHTML = pendingRows.join("");
  if (pendingSection) {
    if (pendingRows.length > 0) pendingSection.classList.remove("hidden");
    else pendingSection.classList.add("hidden");
  }

  // Retired-vault holdings — fire-and-forget client-side on-chain scan. The
  // server never reports retired vaults, so this is what keeps a stranded
  // holder able to withdraw. No-op (section stays hidden) for everyone else.
  _scanRetiredHoldings(addr);

  document.getElementById("total-value").textContent = "$" + totalValue.toFixed(2);
  document.getElementById("total-deposited").textContent = "$" + totalCost.toFixed(2);

  // Total Return — what your shares are worth vs what you deposited.
  // This is the on-chain truth: net of all fees, funding, and vault setup costs.
  // The trades page "Net P&L" (sum of trade fills) will be slightly higher
  // because it doesn't include the one-time $1 HL activation fee or funding.
  const userReturn = totalValue - totalCost;
  _perfDeposited = totalCost;  // used by performance chart baseline
  _perfTotalValue = totalValue;  // used by _updateTotalReturnCard (return vs same value)
  const retEl = document.getElementById("total-return");
  const pctEl = document.getElementById("total-return-pct");
  if (retEl) {
    retEl.textContent = (userReturn >= 0 ? "+$" : "-$") + Math.abs(userReturn).toFixed(2);
    retEl.className = "text-2xl font-headline font-bold " + (userReturn >= 0 ? "text-emerald-400" : "text-error");
  }
  if (pctEl) {
    const pct = totalCost > 0 ? (userReturn / totalCost) * 100 : 0;
    pctEl.textContent = totalCost > 0
      ? `${userReturn >= 0 ? "+" : ""}${pct.toFixed(2)}% since deposit`
      : " ";
  }
}

// ── Retired-vault withdraw scan ───────────────────────────────────────────
// Pure client-side, on-chain. For each status="retired" vault in config.js,
// read the connected wallet's balanceOf + withdrawalRequests directly. Render
// a withdraw/claim card ONLY if the wallet still holds shares or has a pending
// claim. Everyone else: section stays hidden. This restores withdraw access
// that the server intentionally hides (load_fleet is live-only) so retired
// vaults vanish from /api/me/* + the public site. See
// GEMSTONE_RETIREMENT_DESIGN.md §5.
async function _scanRetiredHoldings(addr) {
  const section = document.getElementById("retired-section");
  const body = document.getElementById("retired-body");
  if (!section || !body) return;

  const retired = VAULT_CONFIGS.filter(v => v.status === "retired" && v.address);
  if (!retired.length) return;

  const rp = getReadProvider();
  if (!rp) return;

  const VIRTUAL_OFFSET = 1000n;  // 10^_decimalsOffset (matches live positions math above)

  const cards = await Promise.all(retired.map(async (vc) => {
    try {
      const c = new ethers.Contract(vc.address, VAULT_ABI, rp);
      const [bal, wd, totalAssets, totalSupply, perfFeeBps] = await Promise.all([
        c.balanceOf(addr),
        c.withdrawalRequests(addr),
        c.totalAssets(),
        c.totalSupply(),
        c.performanceFeeBps().catch(() => 0n),
      ]);
      const sharesBn = BigInt(bal);
      const pendingShares = BigInt(wd.shares ?? wd[0] ?? 0);
      if (sharesBn === 0n && pendingShares === 0n) return null;  // not a holder — skip

      const ta = BigInt(totalAssets);
      const denom = BigInt(totalSupply) + VIRTUAL_OFFSET;
      const valueWei = sharesBn > 0n && denom > 0n ? (sharesBn * (ta + 1n)) / denom : 0n;
      const valueHuman = Number(valueWei) / 10 ** USDC_DECIMALS;
      const sharesHuman = Number(sharesBn) / 10 ** SHARE_DECIMALS;

      // Pending claim block (mirrors the live Pending Withdrawals row). V3.6
      // locks the gross payout in snapshottedAssets; use it when > 0.
      let pendingHtml = "";
      if (pendingShares > 0n) {
        const costBasisSnap = BigInt(wd.costBasisSnapshot ?? wd[1] ?? 0);
        const totalSharesSnap = BigInt(wd.totalSharesSnapshot ?? wd[2] ?? 0);
        const snapshottedAssets = BigInt(wd.snapshottedAssets ?? wd[4] ?? 0);
        const grossAssets = snapshottedAssets > 0n
          ? snapshottedAssets
          : (pendingShares * (ta + 1n)) / (BigInt(totalSupply) + pendingShares + VIRTUAL_OFFSET);
        const fee = computeWithdrawalFee(pendingShares, totalSharesSnap, costBasisSnap, grossAssets, BigInt(perfFeeBps));
        const netHuman = Number(grossAssets - fee) / 10 ** USDC_DECIMALS;
        const tail = snapshottedAssets > 0n
          ? "Amount is locked: this is exactly what the contract will pay on claim."
          : "Amount fluctuates with vault NAV until claimed.";
        pendingHtml = `
          <div class="flex justify-between items-center p-3 bg-tertiary/5 rounded-lg mt-2">
            <div class="flex items-center gap-2">
              <span class="text-secondary/80 text-sm">~$${netHuman.toFixed(2)} USDC ready to claim</span>
              <span class="material-symbols-outlined text-secondary/40 text-sm cursor-help" title="${tail}">info</span>
            </div>
            <button class="px-4 py-1.5 bg-tertiary/20 hover:bg-tertiary/30 border border-tertiary/30 rounded-lg text-tertiary font-headline text-sm"
                    onclick="window._claimWithdraw('${vc.address}')">Claim</button>
          </div>`;
      }

      const heldHtml = sharesBn > 0n ? `
        <div class="flex justify-between items-center">
          <div class="text-sm text-on-surface-variant">
            <span class="font-semibold text-on-surface">${sharesHuman.toFixed(4)}</span> shares
            <span class="text-secondary/60">· ≈ $${valueHuman.toFixed(2)}</span>
          </div>
          <button class="px-4 py-1.5 bg-primary/15 hover:bg-primary/25 border border-primary/30 rounded-lg text-primary font-headline text-sm"
                  onclick="window._requestRetiredWithdraw('${vc.address}')">Withdraw all</button>
        </div>` : "";

      return `
        <div class="p-4 bg-surface-container-high/40 rounded-lg mb-2">
          <div class="flex items-center gap-2 mb-2">
            <div class="w-7 h-7 ${vc.color.bg10} rounded-md flex items-center justify-center">
              <span class="material-symbols-outlined ${vc.color.text} text-base">${vc.icon}</span>
            </div>
            <span class="font-headline font-semibold ${vc.color.text}">${vc.name}</span>
            <span class="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-sm bg-surface-container text-on-surface-variant border border-outline-variant/30">Retired</span>
          </div>
          ${heldHtml}
          ${pendingHtml}
        </div>`;
    } catch (e) {
      console.warn(`[retired-scan] read failed for ${vc.name}:`, e);
      return null;
    }
  }));

  const html = cards.filter(Boolean).join("");
  if (html) {
    body.innerHTML = html;
    section.classList.remove("hidden");
  } else {
    section.classList.add("hidden");
  }
}

async function _loadOpenTrades(mePromise, bundlePromise) {
  const tbody = document.getElementById("open-trades-body");
  if (!tbody) return;

  // Scope to vaults the connected user actually owns shares in. Before this
  // change the table fetched positions for EVERY live vault, so a depositor
  // who only owned Onyx saw open trades from Amber/Ruby/etc — confusing and
  // wrong on day 1. /api/me/portfolio (server-side filtered to non-zero
  // stakes) is the authoritative "what vaults does this user own?" source.
  // Falls through to fleet-wide if the promise fails or returns empty —
  // matches the empty state below ("No open positions") for a wallet that
  // has connected but holds no shares.
  let userVaultAddrs = null;
  if (mePromise) {
    try {
      const me = await mePromise;
      userVaultAddrs = new Set((me.vaults || []).map((v) => v.address.toLowerCase()));
    } catch { /* fall through to existing empty-state below */ }
  }

  // Fetch positions for all live vaults, carrying vault config for icon/color.
  // Positions metadata (entry price, SL, TP, side, etc.) comes from the runner's
  // positions.json via the API. Floating P&L + current price are OVERRIDDEN from
  // HL's clearinghouseState (via shared hlFeed cache) so this page shows the
  // exact same numbers as the Trade Log page — they share the same state.
  //
  // Parallelism: was a sequential `for...of` with two awaits per vault =
  // 6 vaults × 2 round-trips × ~300ms = ~3.6s wall. Now Promise.all across
  // vaults AND across (api, hl) within each vault = ~max(300ms) ≈ ~0.3-0.5s
  // wall time. Same merge logic, same fields, just collapsed concurrency.
  let liveVaults = VAULT_CONFIGS.filter(vc => vc.address && vc.status === "live" && (vc.visibility ?? "public") === "public");
  if (userVaultAddrs && userVaultAddrs.size > 0) {
    liveVaults = liveVaults.filter((vc) => userVaultAddrs.has(vc.address.toLowerCase()));
  } else if (userVaultAddrs) {
    // Connected wallet with zero stakes — render the empty state directly,
    // skip the network calls entirely.
    tbody.innerHTML = '<tr><td colspan="8" class="py-8 text-center text-secondary/40">No open positions</td></tr>';
    return;
  }

  // Per-user per-trade attribution: integrates across holding windows so
  // a fresh depositor doesn't see vault-level numbers earned BEFORE they
  // joined. See crystalclear/api/attribution.py.
  //
  // Source ordering: prefer the combined /api/me/overview bundle (1
  // request, 1 cold-start at worst); fall back to the individual
  // /api/me/open-trades-attribution if the bundle is unavailable or
  // its `attribution` slice is null (sub-fetch partial-failure case
  // documented in main.py:get_me_overview).
  const userAddr = getAddress();
  const attributionPromise = (async () => {
    if (!userAddr) return null;
    const bundle = await bundlePromise;
    if (bundle?.attribution) return bundle.attribution;
    try {
      const r = await fetch(`${getApiBase()}/api/me/open-trades-attribution?addr=${userAddr}`);
      return r.ok ? await r.json() : null;
    } catch { return null; }
  })();

  // Positions metadata + HL state: same bundle-first, fallback-individual.
  // The hlFeed cache was already seeded above from bundle.hl_state, so
  // prefetchVaultStates() is a cache-hit no-op when the bundle succeeded.
  const vaultNames = liveVaults.map((vc) => vc.name.toLowerCase()).join(",");
  const vaultAddrs = liveVaults.map((vc) => vc.address);
  const [fleetPositions] = await Promise.all([
    (async () => {
      const bundle = await bundlePromise;
      if (bundle?.positions) return bundle.positions;
      try {
        const r = await fetch(`${getApiBase()}/api/fleet/positions?names=${vaultNames}`);
        return r.ok ? await r.json() : { vaults: {} };
      } catch { return { vaults: {} }; }
    })(),
    prefetchVaultStates(vaultAddrs).catch(() => null),
  ]);

  const perVault = await Promise.all(liveVaults.map(async (vc) => {
    const apiData = (fleetPositions.vaults || {})[vc.name.toLowerCase()] || { positions: [] };
    // getVaultState now returns from cache (warmed by prefetchVaultStates above)
    const hlState = await getVaultState(vc.address).catch(() => ({ assetPositions: [] }));

    const apiPositions = apiData.positions || [];
    const hlByCoin = {};
    for (const entry of (hlState.assetPositions || [])) {
      const p = entry.position || {};
      const sz = Math.abs(parseFloat(p.szi || 0));
      if (sz < 1e-9) continue;
      const posValue = parseFloat(p.positionValue || 0);
      hlByCoin[p.coin] = {
        floating_pnl: parseFloat(p.unrealizedPnl || 0),
        current_price: sz > 0 ? posValue / sz : 0,
        size: sz,
      };
    }

    const out = [];
    const seen = new Set();
    // Merge API metadata with HL truth — HL wins on size/price/pnl.
    for (const pos of apiPositions) {
      const hl = hlByCoin[pos.coin];
      if (!hl) continue;  // HL no longer has this — position closed, skip
      seen.add(pos.coin);
      out.push({
        ...pos,
        floating_pnl: hl.floating_pnl,
        current_price: hl.current_price,
        size: hl.size,
        vc,
      });
    }
    // Include any HL-only positions (unlikely but possible during runner desync)
    for (const coin of Object.keys(hlByCoin)) {
      if (seen.has(coin)) continue;
      const hl = hlByCoin[coin];
      out.push({
        coin, symbol: coin + '/USDT:USDT', side: 'unknown',
        entry_price: 0, sl_price: 0, tp1_price: 0, tp2_price: 0,
        entry_ts: '', timeframe: '',
        floating_pnl: hl.floating_pnl, current_price: hl.current_price, size: hl.size,
        vc,
      });
    }
    return out;
  }));

  const allPositions = perVault.flat();

  // Decomposition into Realized / Unrealized no longer shown on the Overview
  // (moved to the Trades page per industry convention for depositor UIs).
  // See docs/uxP&LResearch in reference memories.

  if (allPositions.length === 0) {
    tbody.innerHTML = '<tr><td colspan="8" class="py-8 text-center text-secondary/40">No open positions</td></tr>';
    return;
  }

  // Resolve the attribution data (already in-flight). Build a lookup
  // map keyed by (vault-name-lower, coin) so each row's render can find
  // its user-specific size + P&L.
  //
  // ALSO track which vaults the attribution endpoint covered. If the user
  // just deposited, the on-chain side (/api/me/portfolio) reflects it
  // immediately, but DDB lags by 30-60s before the indexer + sidecar mirror
  // the deposit event. During that window, attribution returns vaults: []
  // for this user even though they hold shares — and we MUST NOT fall back
  // to vault-level numbers, because that briefly displays the full vault's
  // P&L on a fresh depositor's row ($2.27 of "your" profit when they
  // actually earned ~$0). 30s later attribution catches up, number drops,
  // user thinks "did I lose money?". Same trust-eroding inconsistency we
  // fought through earlier today.
  //
  // Solution: when attribution succeeded as an endpoint (attribution !== null)
  // but the user's vault isn't in its response, show "—" with a tooltip
  // explaining we're calculating. Honest "we don't know yet" beats lying
  // "you earned $X".
  const attribution = await attributionPromise;
  const attrByKey = new Map();
  const attributedVaults = new Set();
  if (attribution && Array.isArray(attribution.vaults)) {
    for (const v of attribution.vaults) {
      attributedVaults.add(v.vault.toLowerCase());
      for (const trade of (v.open_trades || [])) {
        attrByKey.set(`${v.vault.toLowerCase()}:${trade.coin}`, trade);
      }
    }
  }
  // attribution !== null means the endpoint itself succeeded; an empty
  // vault list inside it means "the user has on-chain shares but we
  // haven't indexed their deposit event yet" → SYNC LAG.
  const attributionEndpointOk = attribution !== null;

  tbody.innerHTML = allPositions.map(({ vc, ...pos }) => {
    // LONG/SHORT are directional, not P&L. Cyan vs blue alone read as
    // "almost the same" — both cool-tone. Real distinction comes from
    // SATURATION contrast (vivid vs muted) plus a directional arrow:
    //   LONG  → vivid cyan + ↑  (lit, active)
    //   SHORT → muted grey + ↓  (dimmed, neutral)
    // Grey is not a P&L colour (red/green own that role in the P&L column).
    const sideClass = pos.side === 'long'
      ? 'text-primary border-primary/40 bg-primary/15'
      : 'text-on-surface-variant border-outline-variant/50 bg-surface-container-high';
    const sideArrow = pos.side === 'long' ? '↑' : '↓';
    const sideLabel = `${sideArrow} ${pos.side.toUpperCase()}`;

    // Resolve user-specific size + P&L from the attribution endpoint.
    // Three cases:
    //   (a) Attribution covers this vault + trade  → render user-specific
    //   (b) Attribution endpoint succeeded but the user's vault isn't in
    //       it → DDB sync lag from a very recent deposit. Show "—" with
    //       a tooltip. Never fall back to vault-level numbers here.
    //   (c) Attribution endpoint failed entirely → fall back to vault-
    //       level (old behavior, fine in this case — no inconsistency
    //       between API responses, just a degraded display).
    const attr = attrByKey.get(`${vc.name.toLowerCase()}:${pos.coin}`);
    const vaultName_l = vc.name.toLowerCase();
    const syncPending = attributionEndpointOk && !attributedVaults.has(vaultName_l);

    let displaySize, displayPnl, pnlStr, pnlClass, pnlTooltip, sizeTooltip;
    if (syncPending) {
      // Case (b): show "—" until DDB catches up (typically 30-60s after
      // the deposit landed on-chain).
      displaySize = null;
      displayPnl = null;
      pnlStr = '—';
      pnlClass = 'text-secondary/50';
      pnlTooltip = 'Calculating your share — usually within 60s of a deposit.';
      sizeTooltip = pnlTooltip;
    } else {
      displaySize = attr ? attr.user_effective_size : pos.size;
      displayPnl = attr ? attr.user_pnl_since_deposit : (pos.floating_pnl || 0);
      // Net display P&L of estimated exit fees (exchange + builder; entry
      // fees are already in NAV — this only estimates the exit).
      // Rate: ~0.035% avg exchange + 0.025% builder = 0.06% (builder lowered
      // 3.5→2.5 bps 2026-05-23). Note: when attribution data is present, exit-fee
      // math uses USER's effective size so the estimate scales with the P&L.
      const exitFeeEst = (pos.current_price || 0) * displaySize * 0.0006;
      const pnlNet = displayPnl - exitFeeEst;
      pnlStr = (pnlNet >= 0 ? '+' : '-') + '$' + Math.abs(pnlNet).toFixed(4);
      pnlClass = pnlNet >= 0 ? 'text-emerald-400' : 'text-error';
      const vaultPnl = pos.floating_pnl || 0;
      const vaultSize = pos.size;
      const preDepositExcluded = attr?.user_pnl_breakdown?._pre_deposit_excluded ?? 0;
      pnlTooltip = attr
        ? `Your share of this position's P&L since you deposited. \nVault total on this trade: ${vaultPnl >= 0 ? '+' : '-'}$${Math.abs(vaultPnl).toFixed(4)}. \nPre-deposit chunk (you didn't fund): ${preDepositExcluded >= 0 ? '+' : '-'}$${Math.abs(preDepositExcluded).toFixed(4)}.`
        : `Floating P&L on this position. Vault-level; pro-rata attribution unavailable.`;
      sizeTooltip = attr
        ? `Your effective exposure (your ownership share of the vault's ${vaultSize} ${pos.coin}).`
        : `Vault position size — your effective exposure is a fraction of this.`;
    }
    const sizeStr = displaySize === null ? '—' : Number(displaySize).toFixed(4);

    let entryStr = '—';
    if (pos.entry_ts) {
      const d = new Date(pos.entry_ts);
      entryStr = d.toLocaleString('en-GB', { timeZone: tzZone(), month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' ' + tzLabel(d);
    }

    const priceDecimals = pos.entry_price < 1 ? 4 : pos.entry_price < 10 ? 4 : 2;

    return `
      <tr class="hover:bg-surface-container-high/30 transition-colors">
        <td class="py-3">
          <div class="flex items-center gap-2">
            <div class="w-6 h-6 ${vc.color.bg10} rounded flex items-center justify-center">
              <span class="material-symbols-outlined ${vc.color.text} text-sm">${vc.icon}</span>
            </div>
            <span class="font-headline font-semibold ${vc.color.text} text-sm">${vc.name}</span>
          </div>
        </td>
        <td class="py-3">
          <div class="font-headline font-bold text-on-surface">${pos.coin}</div>
          <div class="text-[0.65rem] text-secondary/50">${entryStr}</div>
        </td>
        <td class="py-3">
          <span class="px-2 py-0.5 rounded text-[0.65rem] font-bold border ${sideClass}">${sideLabel}</span>
        </td>
        <td class="py-3 text-right font-mono">$${pos.entry_price.toFixed(priceDecimals)}</td>
        <td class="py-3 text-right font-mono text-secondary/80">$${pos.current_price > 0 ? pos.current_price.toFixed(priceDecimals) : '—'}</td>
        <td class="py-3 text-right font-mono text-secondary/70">$${pos.sl_price.toFixed(priceDecimals)}</td>
        <td class="py-3 text-right font-mono text-secondary/70" title="${sizeTooltip}">${sizeStr}</td>
        <td class="py-3 text-right font-mono font-semibold ${pnlClass}" title="${pnlTooltip}">${pnlStr}</td>
      </tr>
    `;
  }).join('');
}

// ── Performance Chart ─────────────────────────────────────────────────────
//
// Chart strategy (revised 2026-04-21):
//
//  1. **Wallet connected + has deposit/withdraw history** → show *your* value
//     over time = (your_shares_at_t × share_price_at_t). Uses on-chain events
//     to reconstruct historical share balance (handles multi-deposit users
//     correctly, unlike a naive `current_shares × price` approach).
//
//  2. **Wallet connected + no events in window** → show share-price trajectory,
//     clearly labeled "Vault Share Price" — answers "how is the vault doing"
//     without pretending it's the user's own P&L.
//
//  3. **Wallet not connected** → show vault TVL activity as public context.
//
// Data sources:
//   - `/data/share_price_YYYY-MM.jsonl` (runner's on-chain snapshots every 5m)
//   - `Deposit` / `Withdraw` events from the vault contract, filtered by the
//     user's `owner` address (both events have owner as an indexed topic)
//
// Previously (pre-2026-04-21) this chart used HL's `portfolio` endpoint for the
// vault address, which shows TVL — including other users' deposits/withdrawals.
// That was misleading to a connected user looking at "Your Vault Positions"
// directly above. See memory for context.

let _perfChart = null;
let _perfVaults = [];     // [{vc, data: {perpDay: ..., perpMonth: ...}}] per vault
let _perfDeposited = 0;   // total deposited (for baseline)
let _perfTotalValue = 0;  // on-chain Portfolio Value (for the Total Return card)
let _perfUserTimeline = null;  // [{x:ts, y:value$}] — populated when wallet connected + has events
let _perfSharePriceHistory = null;  // cached JSONL-loaded share price snapshots
let _perfChartMode = "total";  // "total" | "vaults" — toggled by chart-mode-pills
let _perfValueMode = "dollar";  // "dollar" | "percent" — $ value line vs % ROI-on-deposit curve

async function _fetchSharePriceHistory() {
  // Load runner's share_price_YYYY-MM.jsonl snapshots — current + previous month.
  if (_perfSharePriceHistory) return _perfSharePriceHistory;
  const now = new Date();
  const months = [
    `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`,
  ];
  const prev = new Date(now); prev.setUTCMonth(prev.getUTCMonth() - 1);
  months.push(`${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, "0")}`);
  const all = [];
  for (const m of months) {
    try {
      const r = await fetch(`/data/share_price_${m}.jsonl`, { cache: "no-cache" });
      if (!r.ok) continue;
      const text = await r.text();
      for (const line of text.split("\n")) {
        const t = line.trim();
        if (!t) continue;
        try { all.push(JSON.parse(t)); } catch {}
      }
    } catch {}
  }
  if (!all.length) return null;
  _perfSharePriceHistory = all.sort((a, b) => a.ts - b.ts);
  return _perfSharePriceHistory;
}

async function _fetchUserMintBurnEvents(vault, userAddr, provider) {
  // Return sorted [{ts_ms, delta_shares:BigInt, kind}]. Scans last ~23 days
  // (2M blocks) in 1000-block chunks (HL RPC cap). Partial data is OK on
  // rate-limit — we just skip the failing chunk.
  const current = await provider.getBlockNumber();
  const LOOKBACK = 2_000_000;
  const CHUNK = 1000;
  const start = Math.max(0, current - LOOKBACK);
  const depFilter = vault.filters.Deposit(null, userAddr);
  const wdFilter = vault.filters.Withdraw(null, null, userAddr);

  const raw = [];
  for (let b = start; b <= current; b += CHUNK) {
    const end = Math.min(b + CHUNK - 1, current);
    try {
      const [deps, wds] = await Promise.all([
        vault.queryFilter(depFilter, b, end),
        vault.queryFilter(wdFilter, b, end),
      ]);
      for (const e of deps) raw.push({ e, kind: "deposit" });
      for (const e of wds) raw.push({ e, kind: "withdraw" });
    } catch (err) {
      // Rate limit or invalid range — skip, accept partial coverage.
    }
  }
  if (!raw.length) return [];

  // Fetch block timestamps for each distinct block (small set per user)
  const blockNums = [...new Set(raw.map(x => x.e.blockNumber))];
  const tsByBlock = new Map();
  await Promise.all(blockNums.map(async bn => {
    try {
      const blk = await provider.getBlock(bn);
      if (blk) tsByBlock.set(bn, blk.timestamp * 1000);
    } catch {}
  }));

  const events = raw.map(({ e, kind }) => {
    const shares = e.args.shares;  // ethers BigNumber-like
    const delta = kind === "deposit" ? BigInt(shares) : -BigInt(shares);
    return {
      ts_ms: tsByBlock.get(e.blockNumber) || 0,
      delta_shares: delta,
      kind,
      blockNumber: e.blockNumber,
    };
  }).filter(x => x.ts_ms > 0);
  events.sort((a, b) => a.ts_ms - b.ts_ms || a.blockNumber - b.blockNumber);
  return events;
}

function _buildBalanceTimeline(events) {
  // Returns [{ts_ms, balance_shares:BigInt}] starting at 0. Events in time order.
  let balance = 0n;
  const timeline = [{ ts_ms: 0, balance }];
  for (const e of events) {
    balance += e.delta_shares;
    timeline.push({ ts_ms: e.ts_ms, balance });
  }
  return timeline;
}

function _balanceAt(timeline, ts_ms) {
  // Binary search: most recent entry with ts_ms ≤ target (piecewise-constant).
  let lo = 0, hi = timeline.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (timeline[mid].ts_ms <= ts_ms) lo = mid;
    else hi = mid - 1;
  }
  return timeline[lo].balance;
}

async function _computeUserValueTimeline(vc, userAddr) {
  // Server-side per-user timeline. The crystalclear-api's /api/vault/{name}/user_portfolio
  // endpoint reads from a SQLite index that a cron populates incrementally — avoids
  // the 1000-block / 100-req-per-min public-RPC limits that made the client-side
  // eth_getLogs path brittle. Returns { points, events, summary } or null on miss.
  //
  // Falls back to client-side scan (legacy path) if the API is unreachable.
  try {
    const apiBase = getApiBase();
    const url = `${apiBase}/api/vault/${vc.key}/user_portfolio?user=${userAddr}`;
    const r = await fetch(url, { cache: "no-cache" });
    if (r.ok) {
      const data = await r.json();
      if (data.error === "indexer_not_ready") {
        console.warn(`[${vc.name}] indexer not ready yet, falling back to on-chain scan`);
      } else if (data.timeline && data.timeline.length >= 2) {
        const points = data.timeline.map(p => ({ x: p.ts, y: p.value_usd }));
        // Normalize events for the renderer: it expects `ts_ms` and only cares
        // about the two cashflow kinds (deposit + withdrawal_claimed). We also
        // stash the $ amount so the tooltip can show it.
        const USDC_DEC = 1_000_000;
        const events = data.events
          .filter(e => e.kind === "deposit" || e.kind === "withdrawal_claimed")
          .map(e => ({
            ts_ms: e.ts,
            kind: e.kind === "deposit" ? "deposit" : "withdrawal",
            amount_usd: Number(e.assets) / USDC_DEC,
          }));
        return { points, events, summary: data.summary };
      } else if (data.events && data.events.length === 0) {
        return null;  // User genuinely has no deposit history
      }
    }
  } catch (e) {
    console.warn(`[${vc.name}] user_portfolio API failed, falling back:`, e);
  }

  // Fallback — legacy client-side scan (covers ~23 days via 1000-block chunks).
  const provider = getReadProvider();
  if (!provider) return null;
  const vault = new ethers.Contract(vc.address, VAULT_ABI, provider);
  const [events, priceHistory] = await Promise.all([
    _fetchUserMintBurnEvents(vault, userAddr, provider),
    _fetchSharePriceHistory(),
  ]);
  if (!events.length) return null;
  if (!priceHistory || priceHistory.length < 2) return null;

  const timeline = _buildBalanceTimeline(events);
  const firstTs = events[0].ts_ms;
  const USDC_DEC = 6;

  const points = [];
  for (const p of priceHistory) {
    if (p.ts < firstTs) continue;
    const balanceWei = _balanceAt(timeline, p.ts);
    if (balanceWei === 0n) { points.push({ x: p.ts, y: 0 }); continue; }
    const ta = BigInt(p.ta);
    const tsy = BigInt(p.ts_);
    if (tsy === 0n) { points.push({ x: p.ts, y: 0 }); continue; }
    const valueWei = (balanceWei * ta) / tsy;
    points.push({ x: p.ts, y: Number(valueWei) / 10 ** USDC_DEC });
  }
  return { points, events };
}

// Insert a synthetic point at every event timestamp where the vault's
// snapshot timeline doesn't already cover it. Synthetic value =
// (prior snapshot value, or 0) + event delta — i.e. "value follows
// deposits during gaps". Without this, the fleet ratio (value ÷ deposits)
// dives whenever cum_deposits jumps faster than the next snapshot lands,
// producing phantom -50% spikes at every deposit and multi-day -38%
// dips when a vault's snapshot logger starts late after deploy.
function _normalizeTimelinePoints(tl) {
  if (!tl.events || !tl.events.length) return tl;
  const events = tl.events.slice().sort((a, b) => a.ts_ms - b.ts_ms);
  const points = (tl.points || []).slice().sort((a, b) => a.x - b.x);
  const result = [];
  let pIdx = 0;
  for (const e of events) {
    while (pIdx < points.length && points[pIdx].x < e.ts_ms) {
      result.push(points[pIdx]);
      pIdx++;
    }
    // Real snapshot exactly at event ts → use it (no synthetic).
    if (pIdx < points.length && points[pIdx].x === e.ts_ms) {
      result.push(points[pIdx]);
      pIdx++;
      continue;
    }
    const prevValue = result.length ? result[result.length - 1].y : 0;
    const delta = (e.kind === "deposit" ? 1 : -1) * (e.amount_usd || 0);
    result.push({ x: e.ts_ms, y: Math.max(0, prevValue + delta) });
  }
  while (pIdx < points.length) {
    result.push(points[pIdx]);
    pIdx++;
  }
  return { ...tl, points: result };
}

// Merge per-vault user timelines into one fleet-total timeline. Output
// shape:
//   { vc: vc|null, perVault: [{vc, points}], points, events }
// where `points` is the per-timestamp sum across vaults using piecewise-
// constant carry-forward (a vault contributes 0 before its first snapshot
// and its most-recent value thereafter). `perVault` keeps each vault's own
// line so the renderer can draw thin per-vault lines + a thick total.
// Cashflow events are unioned and tagged with the vault name.
function _mergeUserTimelines(timelines) {
  // Close event/snapshot timing gaps before merging (otherwise deposits
  // produce phantom -50% spikes).
  timelines = timelines.map(_normalizeTimelinePoints);
  const perVault = timelines.map(tl => ({ vc: tl.vc, points: tl.points }));

  if (timelines.length === 1) {
    // Single-vault: total IS the vault's line. Tag events anyway so the
    // tooltip can show "Deposit to Onyx" if multi-vault later kicks in.
    const tl = timelines[0];
    const events = (tl.events || []).map(e => ({ ...e, vault: tl.vc.name }));
    return { ...tl, perVault, events };
  }

  const tsSet = new Set();
  for (const tl of timelines) for (const p of tl.points) tsSet.add(p.x);
  const sortedTs = [...tsSet].sort((a, b) => a - b);

  function valueAt(points, ts) {
    if (!points.length || points[0].x > ts) return 0;
    let lo = 0, hi = points.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (points[mid].x <= ts) lo = mid;
      else hi = mid - 1;
    }
    return points[lo].y;
  }

  const points = sortedTs.map(ts => ({
    x: ts,
    y: timelines.reduce((sum, tl) => sum + valueAt(tl.points, ts), 0),
  }));

  const events = [];
  for (const tl of timelines) {
    for (const e of (tl.events || [])) {
      events.push({ ...e, vault: tl.vc.name });
    }
  }
  events.sort((a, b) => a.ts_ms - b.ts_ms);

  return {
    vc: null,
    multi: timelines.map(tl => tl.vc),
    perVault,
    points,
    events,
  };
}

// Attach return curves to a merged timeline. After this runs, every points
// list (fleet + per-vault) has a parallel `returnPoints` list where y is the
// total-return ratio at that timestamp:
//   ratio(t) = (held_value(t) + cash_withdrawn_up_to(t)) / cash_deposited_up_to(t)
// Anchored at 1.00 on first deposit per scope. Each return point also carries
// `valueUsd` so tooltips can show "+2.5% ($175.15)" without a second lookup.
//
// This is the LP "total return on capital" — it counts realized gains (cash you
// withdrew) and divides by gross deposited, so it's the SAME number as the Total
// Return card and is smooth across withdrawals. A fresh deposit still dilutes it
// slightly (money-weighted, honest); pure TWR would hide that.
function _attachReturnCurves(merged) {
  const allEvents = (merged.events || []).slice().sort((a, b) => a.ts_ms - b.ts_ms);

  // Simple total return on capital deployed: (held value + cash withdrawn so far)
  // ÷ cash deposited so far. Counts realized gains, base = gross deposited
  // (stable), and is smooth across withdrawals (a withdrawal moves value→withdrawn,
  // net zero). Matches the Total Return card exactly so the two never disagree.
  function ratioAt(value, ts, events) {
    let dep = 0, wd = 0;
    for (const e of events) {
      if (e.ts_ms > ts) break;
      if (e.kind === "deposit") dep += (e.amount_usd || 0);
      else wd += (e.amount_usd || 0);
    }
    return dep > 0.001 ? (value + wd) / dep : 1;
  }

  merged.returnPoints = (merged.points || []).map(p => ({
    x: p.x,
    y: ratioAt(p.y, p.x, allEvents),
    valueUsd: p.y,
  }));

  for (const pv of (merged.perVault || [])) {
    const vaultEvents = allEvents.filter(e => e.vault === pv.vc.name);
    pv.returnPoints = (pv.points || []).map(p => ({
      x: p.x,
      y: ratioAt(p.y, p.x, vaultEvents),
      valueUsd: p.y,
    }));
  }
}

// Total Return card → LP "total return on capital": (value + withdrawn − deposited)
// ÷ deposited, summed across the user's vaults. Same definition as the % chart
// curve so the headline and the chart always agree. The first paint shows an
// on-chain cost-basis estimate (which ignores realized gains); this refines it to
// the complete number once the per-vault summaries load. No-op if they didn't.
function _updateTotalReturnCard(summaries) {
  if (!summaries || !summaries.length) return;
  let dep = 0, wd = 0, sumVal = 0;
  for (const s of summaries) {
    dep    += Number(s.total_deposited_usd || 0);
    wd     += Number(s.total_withdrawn_usd || 0);
    sumVal += Number(s.current_value_usd   || 0);
  }
  if (dep <= 0.001) return;
  // Prefer the on-chain Portfolio Value already shown on the card so return and
  // value stay internally consistent; fall back to the summaries' value sum.
  const val = _perfTotalValue > 0 ? _perfTotalValue : sumVal;
  const ret = val + wd - dep;          // net profit $, realized + unrealized
  const pct = (ret / dep) * 100;
  const retEl = document.getElementById("total-return");
  const pctEl = document.getElementById("total-return-pct");
  if (retEl) {
    retEl.textContent = (ret >= 0 ? "+$" : "-$") + Math.abs(ret).toFixed(2);
    retEl.className = "text-2xl font-headline font-bold " + (ret >= 0 ? "text-emerald-400" : "text-error");
  }
  if (pctEl) pctEl.textContent = `${ret >= 0 ? "+" : ""}${pct.toFixed(2)}% on deposits`;
}

async function _buildPerformanceChart(mePromise, openTradesReady) {
  // Dispose stale ECharts instance from previous page visit — the SPA re-renders
  // HTML so the old container is gone, but the chart instance still references it.
  if (_perfChart) { try { _perfChart.dispose(); } catch {} _perfChart = null; }
  _perfUserTimeline = null;

  const canvas = document.getElementById("perf-chart");
  if (!canvas) return;

  const liveVaults = VAULT_CONFIGS.filter(v => v.status === "live" && v.address && (v.visibility ?? "public") === "public");
  if (!liveVaults.length) return;

  const userAddr = getAddress();

  // Does this connected wallet currently hold any vault shares? Used to gate
  // the PATH 2 fleet-TVL fallback below — a fresh depositor whose user
  // timeline has < 2 points (deposit just landed, only one share-price
  // snapshot recorded since) would otherwise see all 6 vaults' TVL lines
  // overlaid on their chart. /api/me/portfolio.vaults is server-filtered to
  // non-zero stakes so length>0 ↔ wallet has at least one deposit.
  let userHasStake = false;
  if (mePromise) {
    try {
      const me = await mePromise;
      userHasStake = (me.vaults || []).length > 0;
    } catch { /* assume no stake on error */ }
  }

  // Build per-vault user timelines in parallel, then merge into a single
  // fleet-total line. Each vault's timeline is (your shares × that vault's
  // share price) over time, sampled at the runner's snapshot cadence; the
  // merge sums values across vaults at each timestamp using piecewise-
  // constant carry-forward (a vault contributes from its first snapshot
  // onward). Cashflow events are unioned with vault tags for the tooltip.
  //
  // GATED on openTradesReady: each vault's _computeUserValueTimeline fires
  // a separate /api/vault/<key>/user_portfolio Lambda call. With 6 live
  // vaults that's 6 parallel cold-start candidates. Holding them until
  // the Open Trades table has rendered keeps the table's 1 critical-path
  // call (/api/me/overview) un-contested by the chart's data-load.
  if (openTradesReady) {
    try { await openTradesReady; } catch { /* proceed anyway */ }
  }
  if (userAddr) {
    const summaries = [];
    const perVault = await Promise.all(liveVaults.map(async (vc) => {
      try {
        const tl = await _computeUserValueTimeline(vc, userAddr);
        if (tl && tl.summary) summaries.push(tl.summary);
        if (tl && tl.points && tl.points.length >= 2) return { vc, ...tl };
      } catch (e) {
        console.warn(`User timeline build failed for ${vc.name}:`, e);
      }
      return null;
    }));
    const valid = perVault.filter(t => t !== null);
    if (valid.length) {
      _perfUserTimeline = _mergeUserTimelines(valid);
      _attachReturnCurves(_perfUserTimeline);
    }
    _updateTotalReturnCard(summaries);
  }

  // Still need HL portfolio data for the fallback (no wallet, or connected
  // user has no deposits yet) — fetches in parallel with the user-timeline
  // path above. Cheap enough to always run.
  //
  // BUT: gate these N HL portfolio fetches on the Open Trades render
  // completing, so we don't compete with the table's 3 API calls for
  // Lambda PC=1. Otherwise the chart's 6 hl/info calls + the table's 4
  // = 10 concurrent → 9 cold spillovers → max=5s tail. With this gate
  // the burst is staggered: table (4 calls) → render → chart (6 calls).
  // The chart appearing 0.5-1s after the table feels natural; before
  // this fix both raced and the table got pushed back to ~5s.
  if (openTradesReady) {
    try { await openTradesReady; } catch { /* even on error, proceed */ }
  }
  _perfVaults = [];
  await Promise.all(liveVaults.map(async (vc) => {
    try {
      const resp = await fetch(`${getApiBase()}/api/hl/info`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "portfolio", user: vc.address }),
      });
      const raw = await resp.json();
      const data = {};
      for (const [key, val] of raw) data[key] = val;
      _perfVaults.push({ vc, data });
    } catch (e) {
      console.warn(`Chart: portfolio fetch failed for ${vc.name}:`, e);
    }
  }));

  if (!_perfUserTimeline && !_perfVaults.length) {
    const container = document.getElementById("chart-container");
    if (container) container.innerHTML =
      '<div class="h-72 flex items-center justify-center text-secondary/40 text-sm">Chart data unavailable</div>';
    return;
  }

  // Update the chart title to reflect what's being shown.
  _updatePerfChartHeading();
  _renderPerfChart("perpMonth");

  // Helper: which timeframe pill is currently active. The chart-mode toggle
  // re-renders against the same tf the user last picked.
  function _currentTf() {
    const active = document.querySelector("[data-chart-tf].bg-primary");
    return active?.dataset.chartTf || "perpMonth";
  }

  document.querySelectorAll("[data-chart-tf]").forEach(btn => {
    btn.addEventListener("click", () => {
      document.querySelectorAll("[data-chart-tf]").forEach(b => {
        b.classList.remove("bg-primary", "text-on-primary");
        b.classList.add("bg-surface-container-lowest", "ring-1", "ring-outline-variant/20",
                        "text-on-surface-variant");
      });
      btn.classList.add("bg-primary", "text-on-primary");
      btn.classList.remove("bg-surface-container-lowest", "ring-1", "ring-outline-variant/20",
                           "text-on-surface-variant");
      _renderPerfChart(btn.dataset.chartTf);
    });
  });

  document.querySelectorAll("[data-chart-mode]").forEach(btn => {
    btn.addEventListener("click", () => {
      _perfChartMode = btn.dataset.chartMode;
      document.querySelectorAll("[data-chart-mode]").forEach(b => {
        b.classList.remove("bg-primary", "text-on-primary");
        b.classList.add("bg-surface-container-lowest", "ring-1", "ring-outline-variant/20",
                        "text-on-surface-variant");
      });
      btn.classList.add("bg-primary", "text-on-primary");
      btn.classList.remove("bg-surface-container-lowest", "ring-1", "ring-outline-variant/20",
                           "text-on-surface-variant");
      _renderPerfChart(_currentTf());
      _updatePerfChartHeading();
    });
  });

  document.querySelectorAll("[data-chart-units]").forEach(btn => {
    btn.addEventListener("click", () => {
      _perfValueMode = btn.dataset.chartUnits;
      document.querySelectorAll("[data-chart-units]").forEach(b => {
        b.classList.remove("bg-primary", "text-on-primary");
        b.classList.add("bg-surface-container-lowest", "ring-1", "ring-outline-variant/20",
                        "text-on-surface-variant");
      });
      btn.classList.add("bg-primary", "text-on-primary");
      btn.classList.remove("bg-surface-container-lowest", "ring-1", "ring-outline-variant/20",
                           "text-on-surface-variant");
      _renderPerfChart(_currentTf());
      _updatePerfChartHeading();
    });
  });
}

// Update the "Performance" card heading + subtitle based on which chart mode
// is active. Called from _buildPerformanceChart after data fetch completes.
function _updatePerfChartHeading() {
  const heading = document.querySelector('#chart-tf-pills')?.parentElement?.querySelector('h3, h2, div.flex');
  // We can't easily pick the right element without more DOM knowledge — just
  // add a small <p> subtitle near the canvas if not present.
  const container = document.getElementById("chart-container");
  if (!container) return;
  const existing = container.parentElement?.querySelector('[data-perf-subtitle]');
  if (existing) existing.remove();
  const subtitle = document.createElement('p');
  subtitle.setAttribute('data-perf-subtitle', '1');
  subtitle.className = 'text-[0.65rem] text-secondary/50 mb-2 italic';
  if (_perfUserTimeline) {
    if (_perfValueMode === "percent") {
      subtitle.textContent = 'Your total return on deposits over time — net of all fees, counting money already withdrawn. 0% = breakeven; ↑↓ mark deposits and withdrawals.';
    } else if (_perfChartMode === "vaults") {
      subtitle.textContent = "Each vault's position value over time. ↑↓ mark deposits and withdrawals.";
    } else {
      subtitle.textContent = 'Your position value over time. The dashed line is total deposited — the gap above/below it is your real P&L.';
    }
  } else if (_perfVaults.length) {
    subtitle.textContent = 'Vault TVL activity — includes all depositors. Connect wallet with active deposits to see your own value line.';
  }
  container.parentElement?.insertBefore(subtitle, container);
}

// ↑ / ↓ arrow markers for cashflow points, rendered as ECharts image
// symbols. Color is the vault's brand color so a deposit to Amber shows
// an orange ↑, deposit to Emerald shows green, etc. Cached per
// (kind, color) so we don't re-render the same canvas every redraw.
const _arrowCache = new Map();
function _arrowSymbol(kind, colorHex = "#22d3ee") {
  const key = `${kind}_${colorHex}`;
  const cached = _arrowCache.get(key);
  if (cached) return cached;
  const c = document.createElement("canvas");
  c.width = 20; c.height = 20;
  const ctx = c.getContext("2d");
  ctx.font = "bold 18px Inter, sans-serif";
  ctx.fillStyle = colorHex;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(kind === "deposit" ? "↑" : "↓", 10, 10);
  const url = "image://" + c.toDataURL("image/png");
  _arrowCache.set(key, url);
  return url;
}

function _renderPerfChart(tfKey) {
  // ECharts replaces Chart.js for this chart. Chart.js v4 + MetaMask SES
  // lockdown reliably throws inside bindUserEvents when the chart is built
  // from inside an async init storm (which `_buildPerformanceChart` is).
  // See feedback_chartjs_ses_incompat.md for the debug trail; vault-detail.js
  // was migrated first, this is the second + last call site.
  const container = document.getElementById("chart-container");
  if (!container) return;
  if (typeof echarts === "undefined") {
    console.warn("[perf-chart] ECharts not loaded");
    return;
  }

  const series = [];
  const allPoints = [];

  // Push one main line series + per-vault-colored cashflow scatters for a
  // single (vault, points, cashflows) tuple. Used by the single-vault path
  // and the per-vault TVL fallback.
  function pushVaultSeries(label, vc, points, cashflows, opts = {}) {
    const lineWidth = opts.thick ? 3 : 2;
    series.push({
      name: label,
      type: "line",
      smooth: false,
      showSymbol: false,
      sampling: "lttb",
      lineStyle: { color: vc.color.hex, width: lineWidth },
      areaStyle: opts.noFill ? undefined : {
        color: {
          type: "linear", x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: vc.color.hex + "26" },  // ~15% alpha
            { offset: 1, color: vc.color.hex + "00" },
          ],
        },
      },
      data: points.map(p => ({
        value: [p.x, p.y],
        valueUsd: p.valueUsd,  // present on return-curve points; undefined on raw $ points
      })),
      z: opts.thick ? 10 : 2,
    });
    for (const kind of ["deposit", "withdrawal"]) {
      const items = cashflows.filter(c => c.type === kind);
      if (!items.length) continue;
      series.push({
        // __cf_ prefix → filtered out of legend + tooltip header rendering.
        name: `__cf_${kind}__${label}`,
        type: "scatter",
        symbol: _arrowSymbol(kind, vc.color.hex),
        symbolSize: 20,
        data: items.map(c => ({
          value: [c.point.x, c.point.y],
          cf: c,
          valueUsd: c.point.valueUsd,
        })),
        z: 15,
      });
    }
  }

  // Snap one event to the nearest point on `pts` within ±10 min. Returns
  // null if nothing's close enough.
  function snapEvent(e, pts) {
    let nearest = null, minDist = 10 * 60 * 1000;
    for (const p of pts) {
      const dist = Math.abs(p.x - e.ts_ms);
      if (dist < minDist) { nearest = p; minDist = dist; }
    }
    if (!nearest) return null;
    return {
      point: nearest,
      type: e.kind === "deposit" ? "deposit" : "withdrawal",
      amount: typeof e.amount_usd === "number" ? e.amount_usd : 0,
      vault: e.vault || null,
    };
  }

  // PATH 1 — per-user value timeline (preferred when connected & has deposits).
  if (_perfUserTimeline) {
    const windowMs = {
      perpWeek: 7 * 24 * 3600 * 1000,
      perpMonth: 30 * 24 * 3600 * 1000,
      perpQuarter: 90 * 24 * 3600 * 1000,
      perpAllTime: Infinity,
    }[tfKey] || 30 * 24 * 3600 * 1000;
    const cutoff = Date.now() - windowMs;
    // Dollar values (raw) — y is $ value at each timestamp. In % mode we plot the
    // ROI-on-deposit curve instead (returnPoints — deposit+withdrawal-aware,
    // 0% at first deposit). winPts() falls back to $ if the return curve is
    // missing, so % mode can never break the chart. Cost basis line stays $-only.
    const pct = _perfValueMode === "percent" && !!(_perfUserTimeline.returnPoints);
    const winPts = (dollarPts, retPts) => pct && retPts
      ? retPts.filter(p => p.x >= cutoff).map(p => ({ x: p.x, y: (p.y - 1) * 100, valueUsd: p.valueUsd }))
      : (dollarPts || []).filter(p => p.x >= cutoff);
    const { perVault, points: allTotalPoints, events } = _perfUserTimeline;
    const totalPoints = winPts(allTotalPoints, _perfUserTimeline.returnPoints);
    const eventsInWindow = (events || []).filter(e => e.ts_ms >= cutoff);

    if (totalPoints.length >= 2) {
      const liveVaultsWithData = (perVault || []).filter(pv => {
        return (pv.points || []).some(p => p.x >= cutoff);
      });

      if (liveVaultsWithData.length > 1) {
        // Multi-vault — branch on user's mode toggle.
        if (_perfChartMode === "vaults") {
          // VAULTS mode: per-vault $ value lines, medium thickness, no fill
          // (multiple fills muddy the chart). Each line starts at deposit
          // date with the deposit amount and tracks that vault's value.
          for (const pv of liveVaultsWithData) {
            const vaultPoints = winPts(pv.points, pv.returnPoints);
            if (vaultPoints.length < 2) continue;
            pushVaultSeries(pv.vc.name, pv.vc, vaultPoints, [], { noFill: true, thick: false });
          }
        } else {
          // TOTAL mode (default): one thick cyan $ value line with subtle
          // fill + a stepped dashed cost-basis line that jumps up at each
          // deposit. Gap between value and cost basis = real P&L.
          const totalCashflows = [];
          for (const e of eventsInWindow) {
            const cf = snapEvent(e, totalPoints);
            if (cf) totalCashflows.push(cf);
          }
          series.push({
            name: "Total",
            type: "line",
            smooth: false,
            showSymbol: false,
            sampling: "lttb",
            lineStyle: { color: "#22d3ee", width: 3 },
            areaStyle: {
              color: {
                type: "linear", x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: "#22d3ee26" },
                  { offset: 1, color: "#22d3ee00" },
                ],
              },
            },
            data: totalPoints.map(p => [p.x, p.y]),
            z: 10,
          });
          // Per-vault cashflow scatters (vault color = which vault received the inflow).
          const vaultByName = {};
          for (const pv of liveVaultsWithData) vaultByName[pv.vc.name] = pv.vc;
          const grouped = {};
          for (const cf of totalCashflows) {
            const vn = cf.vault || "_unknown";
            if (!grouped[vn]) grouped[vn] = { deposit: [], withdrawal: [] };
            grouped[vn][cf.type].push({
              value: [cf.point.x, cf.point.y],
              cf,
            });
          }
          for (const [vn, kinds] of Object.entries(grouped)) {
            const vc = vaultByName[vn];
            const colorHex = vc?.color?.hex || "#22d3ee";
            for (const kind of ["deposit", "withdrawal"]) {
              if (!kinds[kind].length) continue;
              series.push({
                name: `__cf_${kind}__${vn}`,
                type: "scatter",
                symbol: _arrowSymbol(kind, colorHex),
                symbolSize: 20,
                data: kinds[kind],
                z: 15,
              });
            }
          }
        }
        allPoints.push(...totalPoints);
      } else {
        // Single-vault: one return line in vault color.
        const lineVc = (liveVaultsWithData[0] && liveVaultsWithData[0].vc)
          || _perfUserTimeline.vc
          || { name: "Your value", color: { hex: "#22d3ee" } };
        const cashflows = [];
        for (const e of eventsInWindow) {
          const cf = snapEvent(e, totalPoints);
          if (cf) cashflows.push(cf);
        }
        pushVaultSeries("Your value", lineVc, totalPoints, cashflows);
        allPoints.push(...totalPoints);
      }
    }
  }

  // PATH 2 — vault TVL fallback (no user timeline available).
  // ONLY for visitors with no current stake (disconnected or never-deposited
  // wallets browsing the marketing-style preview). A depositor whose user
  // timeline is too thin to chart (just deposited, < 2 snapshots yet) would
  // otherwise see fleet-wide TVL lines for vaults they don't own — exactly
  // the bug a fresh tester just reported. Connected-with-stake hits the
  // "data accumulating" placeholder below instead.
  if (!series.length && _perfVaults.length && !userHasStake) {
    for (const { vc, data } of _perfVaults) {
      // perpQuarter (3M) has no matching HL portfolio bucket — fall back to the
      // widest HL series; the x-axis window clips it to the right span anyway.
      const tfData = data[tfKey] || data["perpAllTime"] || data["perpMonth"];
      if (!tfData || !tfData.accountValueHistory) continue;

      const points = tfData.accountValueHistory
        .map(([ts, val]) => ({ x: ts, y: parseFloat(val) }))
        .filter(p => p.y > 0.5)
        .sort((a, b) => a.x - b.x);
      if (points.length < 2) continue;

      // Detect cashflow jumps in TVL: >15% and >$2 absolute.
      const cashflows = [];
      for (let i = 1; i < points.length; i++) {
        const delta = points[i].y - points[i - 1].y;
        if (Math.abs(delta) / points[i - 1].y > 0.15 && Math.abs(delta) > 2.0) {
          cashflows.push({
            point: points[i],
            type: delta > 0 ? "deposit" : "withdrawal",
            amount: Math.abs(delta),
          });
        }
      }

      pushVaultSeries(vc.name, vc, points, cashflows);
      allPoints.push(...points);
    }
  }

  if (!series.length) {
    if (_perfChart) { try { _perfChart.dispose(); } catch {} _perfChart = null; }
    // Different message for a depositor whose data is still warming up vs
    // a generic visitor — the depositor knows they just transacted and
    // expects to see THEIR chart, not "unavailable".
    const msg = userHasStake
      ? 'Your performance data is still accumulating — check back in a few minutes.'
      : 'Chart data unavailable';
    container.innerHTML =
      `<div class="h-full flex items-center justify-center text-secondary/40 text-sm">${msg}</div>`;
    return;
  }

  allPoints.sort((a, b) => a.x - b.x);

  // PATH 1 (Total mode only): stepped dashed cost-basis line. Each deposit
  // bumps cum_deposits up; horizontal between deposits. Drawn alongside the
  // total $ value line so the gap above/below = real P&L (not deposits).
  // Skipped in Vaults mode (multiple vaults, multiple cost bases — clutter).
  if (_perfUserTimeline && _perfChartMode === "total" && allPoints.length && _perfValueMode !== "percent") {
    const cbEvents = ((_perfUserTimeline.events) || []).slice().sort((a, b) => a.ts_ms - b.ts_ms);
    const xLeft = allPoints[0].x;
    const xRight = allPoints[allPoints.length - 1].x;
    let cum = 0;
    // Pre-window deposits already accumulated to the left edge.
    for (const e of cbEvents) {
      if (e.ts_ms >= xLeft) break;
      cum += (e.kind === "deposit" ? 1 : -1) * (e.amount_usd || 0);
    }
    const cbData = [[xLeft, Math.max(0, cum)]];
    for (const e of cbEvents) {
      if (e.ts_ms < xLeft || e.ts_ms > xRight) continue;
      cbData.push([e.ts_ms, Math.max(0, cum)]);  // step horizontal up to deposit ts
      cum += (e.kind === "deposit" ? 1 : -1) * (e.amount_usd || 0);
      cbData.push([e.ts_ms, Math.max(0, cum)]);  // jump to new cum at same ts
    }
    cbData.push([xRight, Math.max(0, cum)]);
    series.push({
      name: "Cost Basis",
      type: "line",
      showSymbol: false,
      lineStyle: { color: "#3c494c80", width: 1, type: "dashed" },
      data: cbData,
      tooltip: { show: false },
      silent: true,
      z: 5,
    });
  }

  // Derive x-axis window from active timeframe button. Force the span
  // explicitly so toggling 1D/7D/1M/ALL reads the way you expect even
  // when data only partially covers the range.
  const now = Date.now();
  const windowMsMap = {
    perpWeek: 7 * 24 * 3600 * 1000,
    perpMonth: 30 * 24 * 3600 * 1000,
    perpQuarter: 90 * 24 * 3600 * 1000,
    perpAllTime: 365 * 24 * 3600 * 1000,
  };
  const windowMs = windowMsMap[tfKey] || windowMsMap.perpMonth;
  let xMin = now - windowMs;
  if (tfKey === "perpAllTime" && allPoints.length) {
    xMin = Math.max(xMin, Math.min(...allPoints.map(p => p.x)) - 3600 * 1000);
  }
  const xMax = now;

  // Init / reuse ECharts instance (mirror vault-detail.js pattern).
  if (!_perfChart || !echarts.getInstanceByDom(container)) {
    container.innerHTML = "";  // nuke stale Chart.js canvas if present
    _perfChart = echarts.init(container, null, { renderer: "canvas" });
    window.addEventListener("resize", () => { if (_perfChart) _perfChart.resize(); });
  }

  // Legend lists every line series (including the "Total" line in
  // multi-vault PATH 1 and per-vault lines in PATH 2). Hidden when there's
  // only one line, since labeling a single line is noise.
  const lineSeriesNames = series
    .filter(s => s.type === "line" && s.name !== "Cost Basis")
    .map(s => s.name);
  const showLegend = lineSeriesNames.length > 1;

  _perfChart.setOption({
    backgroundColor: "transparent",
    textStyle: { color: "#89ceff", fontFamily: "Inter, sans-serif" },
    legend: showLegend
      ? {
          show: true,
          data: lineSeriesNames,
          textStyle: { color: "#89ceff", fontFamily: "Space Grotesk", fontSize: 11 },
          bottom: 0,
        }
      : { show: false },
    grid: { left: 50, right: 20, top: 16, bottom: showLegend ? 40 : 30, containLabel: true },
    tooltip: {
      trigger: "axis",
      backgroundColor: "#1a1f2d",
      borderColor: "#3c494c40",
      borderWidth: 1,
      padding: 10,
      textStyle: { color: "#dee2f5", fontFamily: "Inter", fontSize: 12 },
      axisPointer: { type: "line", lineStyle: { color: "#3c494c" } },
      formatter: (params) => {
        if (!params.length) return "";
        const _d = new Date(params[0].value[0]);
        const ts = _d.toLocaleString("en-GB", {
          timeZone: tzZone(), month: "short", day: "numeric",
          hour: "2-digit", minute: "2-digit",
        }) + " " + tzLabel(_d);
        const lines = [`<div style="font-family:'Space Grotesk';color:#dee2f5">${ts}</div>`];
        const cfLines = [];
        // Sort: Total first (multi-vault PATH 1), then everything else.
        const sorted = [...params].sort((a, b) => {
          if (a.seriesName === "Total") return -1;
          if (b.seriesName === "Total") return 1;
          return 0;
        });
        const multiLine = sorted.filter(i =>
          i.componentType === "series" && i.seriesType === "line" && i.seriesName !== "Cost Basis"
        ).length > 1;
        for (const item of sorted) {
          const name = item.seriesName || "";
          if (name === "Cost Basis") continue;
          if (name.startsWith("__cf_")) {
            const cf = item.data && item.data.cf;
            if (cf) {
              const icon = cf.type === "deposit" ? "↑" : "↓";
              const word = cf.type === "deposit" ? "Deposit" : "Withdrawal";
              const dest = cf.vault ? ` to ${cf.vault}` : "";
              const arrowColor = (item.color && typeof item.color === "string") ? item.color : "#22d3ee";
              cfLines.push(`<div style="color:${arrowColor}">${icon} ${word}${dest}: ~$${cf.amount.toFixed(0)}</div>`);
            }
            continue;
          }
          const v = item.value[1];
          const isPct = _perfValueMode === "percent";
          const label = multiLine ? `${name}: ` : (isPct ? "ROI: " : "Value: ");
          // Use the series stroke color so the legend dot matches the line.
          const c = (item.color && typeof item.color === "string") ? item.color : "#89ceff";
          const vs = isPct ? `${v >= 0 ? "+" : ""}${v.toFixed(2)}%` : `$${v.toFixed(2)}`;
          lines.push(`<div style="color:${c}">${label}${vs}</div>`);
        }
        return [...lines, ...cfLines].join("");
      },
    },
    xAxis: {
      type: "time",
      min: xMin,
      max: xMax,
      axisLine: { lineStyle: { color: "#3c494c" } },
      axisLabel: { color: "#89ceff80", fontSize: 10 },
      splitLine: { lineStyle: { color: "#3c494c15" } },
    },
    yAxis: {
      type: "value",
      scale: true,
      axisLine: { lineStyle: { color: "#3c494c" } },
      axisLabel: {
        color: "#89ceff80", fontSize: 10,
        formatter: (v) => _perfValueMode === "percent"
          ? Number(v).toFixed(0) + "%"
          : "$" + Number(v).toFixed(0),
      },
      splitLine: { lineStyle: { color: "#3c494c15" } },
    },
    series,
  }, true /* not-merge: full replace on tab switch */);
}
